const ignoredWords = new Set(['a', 'an', 'and', 'at', 'in', 'of', 'on', 'the', 'to']);

export const normalizeEntityKey = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

export const eventEntityRecordId = (item = {}) => item.entityId
  || item.id
  || [normalizeEntityKey(item.name || item.title), item.year].filter(Boolean).join('-');

const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const addUtcDays = (value, days) => {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const formatCalendarDate = (value) => new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});

const defaultTimezone = (event = {}) => {
  const location = `${event.city || ''} ${event.country || ''}`.toLowerCase();
  if (location.includes('london') || location.includes('united kingdom')) return 'Europe/London';
  if (location.includes('sydney') || location.includes('australia')) return 'Australia/Sydney';
  if (location.includes('las vegas')) return 'America/Los_Angeles';
  return 'UTC';
};

const datesFromDayNumbers = (event, label) => {
  if (!event?.startDate || !/\bdays?\s+\d/i.test(label)) return [];
  return [...label.matchAll(/\d+/g)].map((match) => addUtcDays(event.startDate, Number(match[0]) - 1));
};

const datesFromWeekdays = (event, label) => {
  if (!event?.startDate) return [];
  const matched = weekdayNames.filter((name) => label.includes(name));
  if (!matched.length) return [];
  const start = new Date(`${event.startDate}T00:00:00Z`);
  const offsets = matched.map((weekday) => (
    (weekdayNames.indexOf(weekday) - start.getUTCDay() + 7) % 7
  ));
  if (matched.length === 2 && /[–-]/.test(label)) {
    const [first, last] = offsets;
    return Array.from({ length: (last - first) + 1 }, (_, index) => addUtcDays(event.startDate, first + index));
  }
  return offsets.map((offset) => addUtcDays(event.startDate, offset));
};

export const eventOccurrences = (event, item = {}) => {
  let explicit = [];
  if (item.occurrences?.length) explicit = item.occurrences;
  else if (item.occurrence) explicit = [item.occurrence];
  if (explicit.length) {
    return explicit.map((occurrence, index) => ({
      timezone: defaultTimezone(event),
      datePrecision: occurrence.startTime ? 'exact' : 'day',
      displayDate: occurrence.startDate ? formatCalendarDate(occurrence.startDate) : '',
      sortTimestamp: occurrence.startDate
        ? `${occurrence.startDate}T${occurrence.startTime || '12:00:00'}`
        : '',
      sequenceOverride: item.order ?? index,
      ...occurrence,
    }));
  }

  const label = item.dayLabel || item.dateLabel || item.date || '';
  const dates = datesFromDayNumbers(event, label).length
    ? datesFromDayNumbers(event, label)
    : datesFromWeekdays(event, label);
  if (dates.length) {
    return dates.map((date, index) => ({
      startDate: date,
      startTime: item.startTime || item.time || '',
      timezone: defaultTimezone(event),
      datePrecision: item.startTime || item.time ? 'approximate' : 'day',
      displayDate: formatCalendarDate(date),
      sortTimestamp: `${date}T${item.startTime || item.time || '12:00:00'}`,
      sequenceOverride: (item.order ?? 0) + (index / 100),
      derived: true,
    }));
  }

  return [{
    startDate: '',
    timezone: defaultTimezone(event),
    datePrecision: 'approximate',
    displayDate: label || '',
    sortTimestamp: '',
    sequenceOverride: item.order ?? 0,
    derived: true,
  }];
};

export const eventDayIndex = (event, item = {}) => {
  if (!event?.startDate) return Number.MAX_SAFE_INTEGER;
  const start = new Date(`${event.startDate}T00:00:00Z`);
  if (item.occurredAt) {
    const occurredAt = new Date(item.occurredAt);
    return Math.floor((occurredAt.getTime() - start.getTime()) / 86400000);
  }
  if (!item.dayLabel) return Number.MAX_SAFE_INTEGER;
  const weekday = weekdayNames.find((name) => item.dayLabel.includes(name));
  if (!weekday) return Number.MAX_SAFE_INTEGER;
  const offset = (weekdayNames.indexOf(weekday) - start.getUTCDay() + 7) % 7;
  return offset;
};

export const eventDateLabel = (event, item = {}) => {
  const occurrence = eventOccurrences(event, item)[0];
  if (occurrence?.displayDate) return occurrence.displayDate;
  if (item.dateLabel || item.date) return item.dateLabel || item.date;
  const label = item.dayLabel || (item.year ? String(item.year) : '');
  if (!event?.startDate || !label) return label;
  const matchedWeekdays = weekdayNames.filter((name) => label.includes(name));
  if (matchedWeekdays.length === 2) {
    const start = new Date(`${event.startDate}T00:00:00Z`);
    const dates = matchedWeekdays.map((weekday) => {
      const date = new Date(start);
      const offset = (weekdayNames.indexOf(weekday) - start.getUTCDay() + 7) % 7;
      date.setUTCDate(date.getUTCDate() + offset);
      return date;
    });
    const month = dates[0].toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    return `${matchedWeekdays.join('–')}, ${month} ${dates[0].getUTCDate()}–${dates[1].getUTCDate()}, ${dates[0].getUTCFullYear()}`;
  }
  if (matchedWeekdays.length !== 1) return event.year ? `${label}, ${event.year}` : label;

  const start = new Date(`${event.startDate}T00:00:00Z`);
  const dayOffset = eventDayIndex(event, item);
  if (!Number.isFinite(dayOffset) || dayOffset === Number.MAX_SAFE_INTEGER) return label;
  start.setUTCDate(start.getUTCDate() + dayOffset);
  const fullDate = start.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  if (/^late\s/i.test(label)) return `Late ${fullDate}`;
  if (/night/i.test(label)) return `${fullDate} · Night`;
  return fullDate;
};

const mediaText = (item = {}) => normalizeEntityKey([
  item.id, item.title, item.name, item.heading, item.alt, item.caption,
].filter(Boolean).join(' '));

const identityKeys = (item = {}, entity = {}) => {
  const resolvedEntity = entity || {};
  return [
    item.entityId,
    item.id,
    item.name,
    item.title,
    item.titleOverride,
    resolvedEntity.id,
    resolvedEntity.name,
  ].filter(Boolean).map(normalizeEntityKey);
};

const matchesIdentity = (asset, item, entity) => {
  const haystack = mediaText(asset);
  return identityKeys(item, entity).some((identity) => {
    if (haystack.includes(identity) || identity.includes(haystack)) return true;
    const tokens = identity.split('-').filter((token) => token.length > 2 && !ignoredWords.has(token));
    return tokens.length > 0 && tokens.every((token) => haystack.includes(token));
  });
};

const normalizeMedia = (asset, index, recordId) => {
  if (!asset) return null;
  if (typeof asset === 'string') return { id: `${recordId}-media-${index + 1}`, src: asset, alt: '' };
  const src = asset.src || asset.image?.src || asset.image;
  if (!src && !asset.firebaseAssetId) return null;
  return {
    ...asset,
    id: asset.id || `${recordId}-media-${index + 1}`,
    ...(src ? { src } : {}),
    alt: asset.alt || asset.imageAlt || '',
  };
};

const directMedia = (item, entity, recordId) => [
  ...(item.images || []),
  ...(item.videos || []).map((video) => ({ ...video, type: 'video' })),
  item.cardImage,
  item.image ? { src: item.image, alt: item.imageAlt, caption: item.imageCaption } : null,
  entity?.heroImage,
].filter(Boolean).map((asset, index) => normalizeMedia(asset, index, recordId)).filter(Boolean);

const uniqueMedia = (media) => {
  const seen = new Set();
  return media.filter((asset) => {
    const identity = asset?.firebaseAssetId || asset?.src?.firebaseAssetId || asset?.src;
    if (!identity || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

export const eventSectionItems = (event, sectionKey) => {
  const source = event || {};
  if (sectionKey === 'dining') {
    return source.restaurants?.length ? source.restaurants : (source.restaurantVisits || []);
  }
  if (sectionKey === 'places') return source.places || [];
  if (sectionKey === 'sessions') return source.sessions || [];
  if (sectionKey === 'venues') return source.venues || [];
  if (sectionKey === 'hotel' || sectionKey === 'hotels') return source.hotels || [];
  return source.activities || [];
};

export const resolveEventEntityDetails = ({
  event, sectionKey, item, entity,
}) => {
  if (!event || !item) return null;
  const recordId = eventEntityRecordId(item);
  const relatedMediaIds = new Set(item.relatedMediaIds || []);
  const taggedMedia = (item.inferMedia === false ? [] : (event.gallery || []))
    .filter(Boolean)
    .filter((asset) => (relatedMediaIds.size
      ? relatedMediaIds.has(asset.id)
      : matchesIdentity(asset, item, entity)))
    .map((asset, index) => normalizeMedia(asset, index, recordId))
    .filter(Boolean);
  const media = uniqueMedia([...taggedMedia, ...directMedia(item, entity, recordId)]);
  const occurrences = eventOccurrences(event, item);
  return {
    id: recordId,
    sectionKey,
    title: item.titleOverride || item.name || item.title || entity?.name || 'Experience',
    description: item.eventDescription || item.description || entity?.shortDescription || '',
    dayLabel: item.dayLabel || (item.year ? String(item.year) : ''),
    dateLabel: eventDateLabel(event, item),
    timeLabel: item.timeLabel || item.time || '',
    occurredAt: item.occurredAt || '',
    occurrences,
    location: item.location || entity?.location || '',
    websiteUrl: item.websiteUrl || entity?.websiteUrl || '',
    mapUrl: item.mapUrl || entity?.mapUrl || '',
    storyItems: [
      ['Why we went', item.whyWeWent],
      ['What happened', item.whatHappened],
      ['What we remember', item.whatWeRemember],
      ['Our take', item.recommendation],
    ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
    storyTitle: item.storyTitle || '',
    storySections: item.storySections || [],
    storyPages: item.storyPages || [],
    media,
  };
};

export const eventEntityRoute = (event, sectionKey, item, storyPageSlug = '') => {
  const params = {
    gallerySlug: event.slug,
    sectionKey,
    entityId: eventEntityRecordId(item),
  };
  const basePageSlug = item.storyPages?.[0]?.slug;
  if (storyPageSlug && storyPageSlug !== basePageSlug) {
    return {
      name: 'reckon-way-entity-story-page',
      params: { ...params, storyPageSlug },
    };
  }
  return { name: 'reckon-way-entity', params };
};
